import { supabase } from '../supabase/config';
import { StaffAllocationGrid } from '../types/grid';
import { transformGridRow } from '../utils/transformGrid';

export class GridsService {
  // Transform database grid data to component-expected structure.
  // Delegates to the shared canonical transform (src/utils/transformGrid.ts) so
  // every context produces the same StaffAllocationGrid shape.
  private transformGridData(dbGrid: any): StaffAllocationGrid {
    return transformGridRow(dbGrid);
  }

  async getAllGrids(): Promise<StaffAllocationGrid[]> {
    try {
      const { data: grids, error } = await supabase
        .from('grids')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform the data to match component expectations
      return (grids || []).map(grid => this.transformGridData(grid));
    } catch (error) {
      console.error('Error fetching grids:', error);
      throw error;
    }
  }

  async getGridsByYear(year: number): Promise<StaffAllocationGrid[]> {
    try {
      const { data: grids, error } = await supabase
        .from('grids')
        .select('*')
        .eq('academic_year', year)
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      return (grids || []).map(grid => this.transformGridData(grid));
    } catch (error) {
      console.error('Error fetching grids by year:', error);
      throw error;
    }
  }

  async getAvailableYears(): Promise<number[]> {
    try {
      const { data, error } = await supabase
        .from('grids')
        .select('academic_year')
        .order('academic_year', { ascending: false });

      if (error) {
        throw error;
      }

      // Extract unique years
      const years = [...new Set(data?.map(g => g.academic_year).filter(Boolean))] as number[];
      return years;
    } catch (error) {
      console.error('Error fetching available years:', error);
      throw error;
    }
  }

  async getGridById(id: string): Promise<StaffAllocationGrid | null> {
    try {
      const { data: grid, error } = await supabase
        .from('grids')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return grid ? this.transformGridData(grid) : null;
    } catch (error) {
      console.error('Error fetching grid by ID:', error);
      throw error;
    }
  }

  async createGrid(grid: Omit<StaffAllocationGrid, 'id' | 'createdAt' | 'updatedAt'>, year?: number): Promise<StaffAllocationGrid> {
    // Use provided year, grid.year, or default to current year
    const academicYear = year || grid.year || new Date().getFullYear();

    try {
      // Transform component data to database structure
      const dbGrid = {
        name: grid.name,
        grid_data: {
          default_class_minutes: grid.defaultClassMinutes,
          lines: grid.lines,
          study_periods: grid.semesters,
          curriculum_areas: grid.curriculumAreas,
          classes: grid.classes
        },
        allocations: grid.classes,
        is_active: grid.isActive,
        academic_year: academicYear,
        term: grid.term,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: newGrid, error } = await supabase
        .from('grids')
        .insert([dbGrid])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return this.transformGridData(newGrid);
    } catch (error) {
      console.error('Error creating grid:', error);
      throw error;
    }
  }

  async updateGrid(id: string, updates: Partial<StaffAllocationGrid>): Promise<StaffAllocationGrid> {
    try {
      // Transform updates to database structure
      const dbUpdates: any = {
        updated_at: new Date().toISOString()
      };

      if (updates.name) dbUpdates.name = updates.name;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
      if (updates.year) dbUpdates.academic_year = updates.year;
      if (updates.term) dbUpdates.term = updates.term;
      // `allocations` is not a column on pit.grids — the real column is `classes`,
      // and grid_data.classes is kept in step below.
      if (updates.classes) dbUpdates.classes = updates.classes;

      if (updates.defaultClassMinutes || updates.lines || updates.semesters || updates.curriculumAreas) {
        // Get current grid_data and update it
        const currentGrid = await this.getGridById(id);
        if (currentGrid) {
          dbUpdates.grid_data = {
            default_class_minutes: updates.defaultClassMinutes || currentGrid.defaultClassMinutes,
            lines: updates.lines || currentGrid.lines,
            study_periods: updates.semesters || currentGrid.semesters,
            curriculum_areas: updates.curriculumAreas || currentGrid.curriculumAreas,
            classes: updates.classes || currentGrid.classes
          };
        }
      }

      const { data: updatedGrid, error } = await supabase
        .from('grids')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return this.transformGridData(updatedGrid);
    } catch (error) {
      console.error('Error updating grid:', error);
      throw error;
    }
  }

  async deleteGrid(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('grids')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting grid:', error);
      throw error;
    }
  }

  async duplicateGrid(id: string, newName: string): Promise<StaffAllocationGrid> {
    try {
      // First get the original grid
      const originalGrid = await this.getGridById(id);
      if (!originalGrid) {
        throw new Error('Grid not found');
      }

      // Create a copy with a new name
      const { id: _, createdAt, updatedAt, ...gridData } = originalGrid;
      const duplicatedGrid = await this.createGrid({
        ...gridData,
        name: newName
      });

      return duplicatedGrid;
    } catch (error) {
      console.error('Error duplicating grid:', error);
      throw error;
    }
  }

  /**
   * Create a composite class from multiple classes
   * Returns the updated grid data without persisting to database
   * @param gridId - The ID of the grid
   * @param compositeName - The name for the new composite class
   * @param subClassIds - Array of class IDs to combine into the composite
   */
  async createCompositeClass(gridId: string, compositeName: string, subClassIds: string[]): Promise<StaffAllocationGrid> {
    try {
      const grid = await this.getGridById(gridId);
      if (!grid) {
        throw new Error('Grid not found');
      }

      // Find the sub-classes
      const subClasses = grid.classes.filter(c => subClassIds.includes(c.id));
      if (subClasses.length === 0) {
        throw new Error('No valid classes found to create composite');
      }

      // Generate new ID for composite class
      const compositeId = `composite_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create the composite class
      // Copy line assignments from the first selected class
      const firstClass = subClasses[0];
      const compositeClass = {
        id: compositeId,
        className: compositeName,
        curriculumArea: firstClass.curriculumArea,
        yearLevel: firstClass.yearLevel,
        level: firstClass.level,
        subjectCode: firstClass.subjectCode,
        lineAssignments: { ...firstClass.lineAssignments },
        department: firstClass.department,
        classMinutes: firstClass.classMinutes,
        isComposite: true,
        compositeSubClasses: subClassIds,
      };

      // Update the sub-classes to mark them as part of composite
      const updatedClasses = grid.classes.map(c => {
        if (subClassIds.includes(c.id)) {
          return {
            ...c,
            isCompositeSubClass: true,
            parentCompositeId: compositeId,
          };
        }
        return c;
      });

      // Add the composite class to the classes array
      updatedClasses.push(compositeClass);

      // Return the modified grid without persisting (let the component handle the update)
      return {
        ...grid,
        classes: updatedClasses
      };
    } catch (error) {
      console.error('Error creating composite class:', error);
      throw error;
    }
  }

  /**
   * Break apart a composite class into its original sub-classes
   * Returns the updated grid data without persisting to database
   * @param gridId - The ID of the grid
   * @param compositeClassId - The ID of the composite class to break apart
   */
  async breakApartCompositeClass(gridId: string, compositeClassId: string): Promise<StaffAllocationGrid> {
    try {
      const grid = await this.getGridById(gridId);
      if (!grid) {
        throw new Error('Grid not found');
      }

      // Find the composite class
      const compositeClass = grid.classes.find(c => c.id === compositeClassId && c.isComposite);
      if (!compositeClass) {
        throw new Error('Composite class not found');
      }

      // Restore the sub-classes (remove composite markers)
      const updatedClasses = grid.classes
        .filter(c => c.id !== compositeClassId) // Remove the composite class
        .map(c => {
          if (c.parentCompositeId === compositeClassId) {
            // Restore the sub-class
            const { isCompositeSubClass, parentCompositeId, ...restoredClass } = c;
            return restoredClass;
          }
          return c;
        });

      // Return the modified grid without persisting (let the component handle the update)
      return {
        ...grid,
        classes: updatedClasses
      };
    } catch (error) {
      console.error('Error breaking apart composite class:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const gridsService = new GridsService();